import {gql} from "@apollo/client";
import {useLazyQuery$} from "../../../graphqlsupport";
import {ReportServiceType} from "../domain";
import {useEffect} from "react";
import {UseFormReturn} from "react-hook-form";
import {LatestOfferedHourData, usePopulateLatestOfferedHours} from "../staffing-plan/useLatestOfferedHours";

export type LatestProviderTransitionRequestCase = {
    sfId: string;
    caseNumber: string;
    status: string;
    serviceTypes: string[];
    caregiverMediatedMidLevelMin: number | null;
    caregiverMediatedMidLevel: number | null;
    caregiverMediatedHighLevelMin: number | null;
    caregiverMediatedHighLevel: number | null;
    pracMediatedDirectCareBeneficial: number | null;
    pracMediatedDirectCareOptimal: number | null;
    pracMediatedMidLevelBeneficial: number | null;
    pracMediatedMidLevelOptimal: number | null;
    pracMediatedHighLevelBeneficial: number | null;
    pracMediatedHighLevelOptimal: number | null;
    pracMediatedGroupCareBeneficial: number | null;
    pracMediatedGroupCareOptimal: number | null;
};

const AUTHORIZATION_LATEST_OFFERED_HOURS_FOR_CLIENT_REPORT = gql`
query authorizationLatestOfferedHoursForClientReport($authorizationGuid: UUID) {
    authorizationLatestOfferedHours(authorizationGuid: $authorizationGuid) {
        reportType
        serviceType
        addendumRequestReason

        caregiverMidLevelHoursMin
        caregiverMidLevelHoursMax
        caregiverHighLevelHoursMin
        caregiverHighLevelHoursMax
        practitionerParaprofessionalHoursMin
        practitionerParaprofessionalHoursMax
        practitionerMidLevelHoursMin
        practitionerMidLevelHoursMax
        practitionerHighLevelHoursMin
        practitionerHighLevelHoursMax
        practitionerGroupCareHoursMin
        practitionerGroupCareHoursMax
        offeredParaprofessionalHours
        offeredMidLevelHours
        offeredHighLevelHours
        offeredGroupCareHours
        currentOfferedParaprofessionalHours
        currentOfferedMidLevelHours
        currentOfferedHighLevelHours
        currentOfferedGroupCareHours
    }
    latestProviderTransitionRequestCaseByAuthorizationGuid(authorizationGuid: $authorizationGuid) {
        sfId
        caseNumber
        status
        serviceTypes
        caregiverMediatedMidLevelMin
        caregiverMediatedMidLevel
        caregiverMediatedHighLevelMin
        caregiverMediatedHighLevel
        pracMediatedDirectCareBeneficial
        pracMediatedDirectCareOptimal
        pracMediatedMidLevelBeneficial
        pracMediatedMidLevelOptimal
        pracMediatedHighLevelBeneficial
        pracMediatedHighLevelOptimal
        pracMediatedGroupCareBeneficial
        pracMediatedGroupCareOptimal
    }
}
`;

type QueryInput = {
    authorizationGuid?: string;
};

type QueryResult = {
    authorizationLatestOfferedHours: LatestOfferedHourData;
    latestProviderTransitionRequestCaseByAuthorizationGuid: LatestProviderTransitionRequestCase | null;
};

function useLatestOfferedHoursForClientReportQuery$(authorizationGuid?: string) {
    const {execute, data, isRunning$, error} = useLazyQuery$<QueryInput, QueryResult>(
        AUTHORIZATION_LATEST_OFFERED_HOURS_FOR_CLIENT_REPORT,
        authorizationGuid == null ? {} : {authorizationGuid},
    );

    return {execute, data, isRunning$, error};
}

export function useLatestOfferedHoursForClientReport<T>(showModal: boolean, methods: UseFormReturn<T>, authorizationGuid?: string, serviceTypes: ReportServiceType[] = []) {
    const {execute, data, isRunning$, error} = useLatestOfferedHoursForClientReportQuery$(authorizationGuid);

    useEffect(() => {
        if (showModal && authorizationGuid != null && authorizationGuid !== '') {
            execute();
        }
    }, [authorizationGuid, showModal, execute]);

    usePopulateLatestOfferedHours(data?.authorizationLatestOfferedHours, methods, serviceTypes);

    return {
        data,
        isRunning$,
        error,
        transitionRequestData: data?.latestProviderTransitionRequestCaseByAuthorizationGuid ?? null,
    };
}
